"use client";

import { Pencil, Plus } from "lucide-react";
import { useState } from "react";

import { pipelineStageIcon } from "@/app/(authenticated)/leads/_modules/constants/pipeline-stage-icons";
import { useCreatePipelineStage } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { Button } from "@/app/_module/components/ui/button";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ResponsiveDialogContent,
} from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";
import {
  MAX_QUICK_DISPOSITIONS,
  useQuickDispositions,
  useSaveQuickDispositions,
} from "@/app/_module/hooks/useQuickDispositions";
import { cn } from "@/app/_module/lib/utils";
import { showToast } from "@/app/_module/utils/show-toast";

interface QuickDispositionButtonsProps {
  /** Gets the lead status to set. Dialers treat it like Bad #: file the lead, move on. */
  onPick: (status: string) => void;
  disabled?: boolean;
  /** Min height/text/icon size of the Bad # / DNC / No Sale row it sits under. */
  buttonClassName?: string;
}

/** Picks pipelines for the buttons. Pick order = button order. Rows look like the stage manager's. */
const EditQuickDispositions = function ({ onClose }: { onClose: () => void }) {
  const { buttons, savedIds, agencyStageIds, stages, ready } = useQuickDispositions();
  const save = useSaveQuickDispositions();
  const createStage = useCreatePipelineStage();
  // null until the agent changes something, so a slow load can't leave it empty.
  const [touched, setTouched] = useState<string[] | null>(null);
  const [newName, setNewName] = useState("");
  const picked = touched ?? buttons.map((b) => b.id);
  const full = picked.length >= MAX_QUICK_DISPOSITIONS;

  const toggle = (id: string) =>
    setTouched(picked.includes(id) ? picked.filter((x) => x !== id) : full ? picked : [...picked, id]);

  const addPipeline = () => {
    const name = newName.trim();
    if (!name) return;
    createStage.mutate(
      { name, color: "#3b82f6" },
      {
        onSuccess: (stage: { id?: string } | undefined) => {
          setNewName("");
          const id = stage?.id;
          if (id) setTouched((t) => {
            const cur = t ?? picked;
            return cur.length >= MAX_QUICK_DISPOSITIONS ? cur : [...cur, id];
          });
        },
      },
    );
  };

  const onSave = () => {
    // Picks made under the agent's other agencies stay saved, after this agency's.
    const elsewhere = savedIds.filter((id) => !agencyStageIds.has(id));
    save.mutate([...picked, ...elsewhere], {
      onSuccess: () => {
        showToast("Disposition buttons saved", "success");
        onClose();
      },
      onError: (e: Error) => showToast(e.message || "Couldn't save your buttons", "error"),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <ResponsiveDialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Your disposition buttons</DialogTitle>

          <DialogDescription>
            Up to {MAX_QUICK_DISPOSITIONS} pipelines, in the order you pick them. One click files the lead there
            and moves on, like Bad #.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between px-0.5 text-xs text-muted-foreground">
            <span className="font-medium">Pipelines</span>

            <span className="tabular-nums">
              {picked.length} of {MAX_QUICK_DISPOSITIONS} picked
            </span>
          </div>

          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {stages.map((s) => {
              const n = picked.indexOf(s.id);
              const on = n >= 0;
              const locked = !on && full;
              const Icon = pipelineStageIcon(s.icon);
              return (
                <label
                  key={s.id}
                  className={cn(
                    "flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors",
                    // Opaque mix, not border-primary/40: translucent borders fringe at rounded corners in Chrome.
                    on
                      ? "border-[color-mix(in_oklab,var(--primary)_40%,var(--background))] bg-primary/5"
                      : "border-border hover:bg-accent/60",
                    locked ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                  )}
                >
                  <Checkbox checked={on} disabled={locked} onCheckedChange={() => toggle(s.id)} />

                  <span
                    className="flex size-6 shrink-0 items-center justify-center rounded-md"
                    style={{ backgroundColor: s.color }}
                  >
                    <Icon aria-hidden className="size-3.5 text-white" />
                  </span>

                  <span className="flex-1 truncate text-sm font-medium">{s.name}</span>

                  {on ? (
                    <span
                      className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold tabular-nums text-primary-foreground"
                      title={`Button ${n + 1}`}
                    >
                      {n + 1}
                    </span>
                  ) : null}
                </label>
              );
            })}
          </div>
        </div>

        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            addPipeline();
          }}
        >
          <Input
            aria-label="New pipeline name"
            maxLength={50}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New pipeline"
            value={newName}
          />

          <Button disabled={!newName.trim() || createStage.isPending} type="submit" variant="outline">
            <Plus aria-hidden />
            {createStage.isPending ? "Adding..." : "Add"}
          </Button>
        </form>

        <DialogFooter className="gap-2">
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>

          <Button disabled={!ready || save.isPending} onClick={onSave}>
            {save.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </ResponsiveDialogContent>
    </Dialog>
  );
};

/**
 * The agent's own disposition buttons (pipelines they picked), under Bad # / DNC / No Sale: the same
 * outline buttons, each with its pipeline's own icon in the pipeline's color (as on the Leads page and in
 * the stage manager). The label stays neutral so a row of pipelines reads calmly under the red one.
 */
export const QuickDispositionButtons = function ({
  onPick,
  disabled = false,
  buttonClassName = "min-h-8 text-xs [&_svg]:size-3",
}: QuickDispositionButtonsProps) {
  const { buttons } = useQuickDispositions();
  const [editing, setEditing] = useState(false);
  const hasButtons = buttons.length > 0;

  return (
    <div>
      {hasButtons ? (
        <div className="grid grid-cols-3 gap-1.5">
          {buttons.map((b) => {
            const Icon = pipelineStageIcon(b.icon);
            return (
              <Button
                key={b.id}
                // Long names wrap beside the icon (truncate cut "Screened Call" in the 400px widget). px-1 is what
                // keeps the three defaults on one line in that widget's thirds.
                className={cn(
                  buttonClassName,
                  "h-auto gap-1 whitespace-normal px-1 py-1 leading-tight text-foreground/80 hover:text-foreground",
                )}
                disabled={disabled}
                onClick={() => onPick(b.value)}
                size="sm"
                title={`Move to ${b.name}`}
                variant="outline"
              >
                <Icon aria-hidden style={{ color: b.color }} />

                <span className="min-w-0 text-left wrap-break-word">{b.name}</span>
              </Button>
            );
          })}
        </div>
      ) : null}

      <div className={cn("flex justify-end", hasButtons && "mt-1")}>
        <button
          className="inline-flex items-center gap-1 rounded-sm text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => setEditing(true)}
          type="button"
        >
          {hasButtons ? <Pencil aria-hidden className="size-3" /> : <Plus aria-hidden className="size-3" />}
          {hasButtons ? "Edit buttons" : "Add your own buttons"}
        </button>
      </div>

      {editing ? <EditQuickDispositions onClose={() => setEditing(false)} /> : null}
    </div>
  );
};
